<template lang="pug">
.question-block-editor
  .editor-header
    h3 {{ isCreating ? 'New Question' : 'Question' }}

  .editor-form
    el-form(:model="formData" label-position="top" :rules="rules" ref="formRef")
      template(v-if="isCreating || !formData.questionId")
        el-form-item(label="Question" prop="questionContent")
          el-input(
            v-model="formData.questionContent"
            placeholder="Enter question content"
            size="default"
          )

        el-form-item(label="Organization (Optional)")
          .organization-selector
            .selected-organization(v-if="formData.organizationId && organizationData")
              .selected-organization-content
                .selected-organization-thumbnail
                  img(
                    v-if="organizationData.avatar"
                    :src="organizationData.avatar"
                  )
                  .no-image(v-else)
                .selected-organization-info
                  .selected-organization-title {{ organizationData.name }}
              .selected-organization-actions
                el-button(
                  type="primary"
                  size="small"
                  @click="showOrganizationSelector = true"
                ) Change
                el-button(type="danger" size="small" plain @click="clearSelectedOrganization") Clear

            .no-organization-selected(v-else)
              el-button(type="primary" plain @click="showOrganizationSelector = true")
                i.el-icon-plus
                | Select Organization

        .editor-actions
          el-button(
            @click="handleCancel"
            size="default"
          ) Cancel
          el-button(
            type="primary"
            @click="handleSave"
            size="default"
            :loading="saving"
          ) Save

      template(v-else)
        .question-display
          h4 Question:
          p {{ formData.text }}
        // Organization selector for existing question
        el-form-item(label="Organization (Optional)")
          .organization-selector
            .selected-organization(v-if="formData.organizationId && organizationData")
              .selected-organization-content
                .selected-organization-thumbnail
                  img(
                    v-if="organizationData.avatar"
                    :src="organizationData.avatar"
                  )
                  .no-image(v-else)
                .selected-organization-info
                  .selected-organization-title {{ organizationData.name }}
              .selected-organization-actions
                el-button(
                  type="primary"
                  size="small"
                  @click="showOrganizationSelector = true"
                ) Change
                el-button(type="danger" size="small" plain @click="clearSelectedOrganization") Clear
            .no-organization-selected(v-else)
              el-button(type="primary" plain @click="showOrganizationSelector = true")
                i.el-icon-plus
                | Select Organization

        .editor-actions
          el-button(
            @click="handleCancel"
            size="default"
          ) Cancel
          el-button(
            type="primary"
            @click="handleSave"
            size="default"
            :loading="saving"
          ) Save
  // Organization selector dialog
  DialogSelectSingleOrganization(
    v-model="showOrganizationSelector"
    :preSelectedOrganization="formData.organizationId"
    :excludeUnclaimed="true"
    @confirm="handleOrganizationSelected"
    @clear="clearSelectedOrganization"
  )
</template>

<script>
import {
  defineComponent, ref, reactive, watch, computed,
} from 'vue';
import { ElMessage } from 'element-plus';
import useStaticPageUtils from '../shared/index.ts';
import DialogSelectSingleOrganization from '@/components/DialogSelectSingleOrganization.vue';

export default defineComponent({
  name: 'QuestionBlock',
  components: { DialogSelectSingleOrganization },
  props: {
    block: {
      type: Object,
      default: null,
    },
    isCreating: {
      type: Boolean,
      default: false,
    },
    pageId: {
      type: String,
      required: true,
    },
    staticPageData: {
      type: Object,
      default: null,
    },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const { createFeaturedQuestion, createOrUpdateQuestionBlock } = useStaticPageUtils();
    const formRef = ref(null);
    const saving = ref(false);
    const showOrganizationSelector = ref(false);

    // Store organization selected from dialog that might not be in staticPageData yet
    const dialogSelectedOrganization = ref(null);

    // Form data
    const formData = reactive({
      questionId: props.block?.details?.questionId || '',
      organizationId: props.block?.details?.organizationId || '',
      text: props.block?.details?.text || '',
      questionContent: '', // Only used when creating a new question
    });

    // Watch for changes to the block prop and update formData
    watch(() => props.block, (newBlock) => {
      if (newBlock && newBlock.details) {
        formData.questionId = newBlock.details.questionId || '';
        formData.organizationId = newBlock.details.organizationId || '';
        formData.text = newBlock.details.text || '';
      }
    }, { deep: true, immediate: true });

    // Form validation rules
    const rules = {
      questionContent: [
        { required: true, message: 'Please enter question content', trigger: 'blur' },
      ],
    };

    // Get organization data from static page data or dialog selection
    const organizationData = computed(() => {
      if (!formData.organizationId) {
        return null;
      }

      // First try to find in dialog selected organization
      if (dialogSelectedOrganization.value
          && dialogSelectedOrganization.value.id === formData.organizationId) {
        return dialogSelectedOrganization.value;
      }

      // Then try to find in static page data
      if (props.staticPageData?.organizations) {
        return props.staticPageData.organizations
          .find((org) => org.id === formData.organizationId) || null;
      }

      return null;
    });

    // Handle organization selection
    const handleOrganizationSelected = (organizationId, organizationObject = null) => {
      formData.organizationId = organizationId;
      // Store the full organization object for display
      dialogSelectedOrganization.value = organizationObject;
    };

    // Clear selected organization
    const clearSelectedOrganization = () => {
      formData.organizationId = '';
      dialogSelectedOrganization.value = null;
    };

    // Handle save
    async function handleSave() {
      if (!formRef.value) return;

      try {
        await formRef.value.validate();
        saving.value = true;
        let input;
        if (props.isCreating || !formData.questionId) {
          // First create the question
          const questionResult = await createFeaturedQuestion({
            content: formData.questionContent,
          });
          // Then create the question block using the new question ID
          input = {
            questionId: questionResult.id,
            organizationId: formData.organizationId || null,
            parentId: props.pageId,
            sortOrder: null, // Will be calculated automatically for new blocks
          };
        } else {
          // Just update the organization for existing question
          input = {
            id: props.block.id,
            questionId: formData.questionId,
            organizationId: formData.organizationId || null,
            parentId: props.pageId,
            sortOrder: props.block.sortOrder || 0,
          };
        }

        const result = await createOrUpdateQuestionBlock(input, props.staticPageData);

        ElMessage.success(props.isCreating ? 'Question block created successfully' : 'Question block updated successfully');
        emit('save', result);
      } catch (error) {
        console.error('Error saving question block:', error);
        ElMessage.error('Failed to save question block');
      } finally {
        saving.value = false;
      }
    }

    // Handle cancel
    function handleCancel() {
      emit('cancel');
    }

    return {
      formRef,
      formData,
      rules,
      saving,
      handleSave,
      handleCancel,
      showOrganizationSelector,
      organizationData,
      dialogSelectedOrganization,
      handleOrganizationSelected,
      clearSelectedOrganization,
    };
  },
});
</script>

<style scoped>
.question-block-editor {
  width: 100%;
}

.editor-form {
  max-width: 500px;
  margin-top: 20px;
}

.question-display {
  background-color: #f8f9fa;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.question-display h4 {
  margin-top: 0;
  margin-bottom: 8px;
  font-weight: 600;
  color: #303133;
}

.question-display p {
  margin: 0 0 16px 0;
  color: #606266;
}

.organization-selector {
  margin-bottom: 20px;
}

.selected-organization {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border: 1px solid #e1f5fe;
  border-radius: 4px;
  background-color: #f0f9ff;
}

.selected-organization-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selected-organization-thumbnail {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.selected-organization-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e0e0e0;
  color: #606266;
  font-size: 12px;
}

.selected-organization-info {
  display: flex;
  flex-direction: column;
}

.selected-organization-title {
  font-weight: bold;
  color: #303133;
}

.selected-organization-actions {
  display: flex;
  gap: 8px;
}

.no-organization-selected {
  display: flex;
  justify-content: center;
  padding: 15px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
}

.organization-info {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.question-id {
  font-size: 12px;
  color: #909399;
  font-family: monospace;
}

.editor-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
</style>
